// AGC: Admin Panel - Crop Edit Modal Component
// AGC: Modal for editing individual crop requirements
// AGC: NOW INCLUDES: Image upload for LOD card backgrounds

import React, { useState, useEffect, useRef } from 'react'
import { getStorage, ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage'
import type { CropRequirements, GrowthStage } from '../../../types/admin/models'

interface CropEditModalProps {
  crop: CropRequirements | null
  isOpen: boolean
  onClose: () => void
  onSave: (crop: CropRequirements) => void
}

/**
 * AGC: Compress image to reduce file size
 * AGC: Max 1920px width, 85% quality for LOD card backgrounds
 */
const compressImage = async (file: File, maxWidth: number = 1920): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const reader = new FileReader()
    
    reader.onload = (e) => {
      img.src = e.target?.result as string
    }
    
    reader.onerror = reject
    
    img.onload = () => {
      const canvas = document.createElement('canvas')
      let width = img.width
      let height = img.height
      
      // AGC: Scale down if too large
      if (width > maxWidth) {
        height = (height * maxWidth) / width
        width = maxWidth
      }
      
      canvas.width = width
      canvas.height = height
      
      const ctx = canvas.getContext('2d')
      if (!ctx) {
        reject(new Error('Failed to get canvas context'))
        return
      }
      
      ctx.drawImage(img, 0, 0, width, height)
      
      canvas.toBlob(
        (blob) => {
          if (blob) {
            resolve(blob)
          } else {
            reject(new Error('Failed to compress image'))
          }
        },
        'image/jpeg',
        0.85 // 85% quality
      )
    }
    
    img.onerror = reject
    reader.readAsDataURL(file)
  })
}

/**
 * AGC: CropEditModal - Edit crop climate requirements
 * AGC: Full modal form for editing all crop parameters including growth stages and image
 */
export const CropEditModal: React.FC<CropEditModalProps> = ({
  crop,
  isOpen,
  onClose,
  onSave,
}) => {
  const [editedCrop, setEditedCrop] = useState<CropRequirements | null>(null)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  // AGC: TEMPORARY WORKAROUND - Manual URL input (until Firebase Storage accessible)
  const [manualImageUrl, setManualImageUrl] = useState('')
  const [useManualUrl, setUseManualUrl] = useState(false)

  // AGC: Reset form when crop changes
  useEffect(() => {
    if (crop) {
      setEditedCrop({ ...crop })
      setImagePreview(crop.imageUrl || null)
      setSelectedImage(null)
      setUploadProgress(0)
    }
  }, [crop])

  if (!isOpen || !editedCrop) return null

  const handleChange = (field: keyof CropRequirements, value: string | number) => {
    setEditedCrop(prev => prev ? { ...prev, [field]: value } : null)
  }

  const handleStageChange = (index: number, field: keyof GrowthStage, value: string | number) => {
    if (!editedCrop) return
    const newStages = [...editedCrop.growthStages]
    newStages[index] = { ...newStages[index], [field]: value }
    setEditedCrop({ ...editedCrop, growthStages: newStages })
  }
  
  // AGC: Handle image file selection
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    
    // AGC: Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Por favor, selecione uma imagem válida')
      return
    }
    
    // AGC: Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('Imagem muito grande. Máximo 10MB')
      return
    }
    
    setSelectedImage(file)
    
    // AGC: Create preview
    const reader = new FileReader()
    reader.onload = (e) => {
      setImagePreview(e.target?.result as string)
    }
    reader.readAsDataURL(file)
  }
  
  // AGC: Upload image to Firebase Storage
  const uploadImage = async (): Promise<string> => {
    if (!selectedImage || !editedCrop) {
      throw new Error('No image selected')
    }
    
    try {
      const storage = getStorage()
      console.log('🔥 AGC: Firebase Storage initialized')
      
      const filePath = `crop-images/${editedCrop.id}.jpg`
      const storageRef = ref(storage, filePath)
      console.log(`📁 AGC: Storage path: ${filePath}`)
      
      // AGC: Compress image first
      console.log('🖼️ AGC: Compressing image...')
      setUploadProgress(5) // AGC: Show compression progress
      const compressedBlob = await compressImage(selectedImage)
      console.log(`✅ AGC: Compressed from ${(selectedImage.size / 1024).toFixed(1)} KB to ${(compressedBlob.size / 1024).toFixed(1)} KB`)
      
      // AGC: Upload with progress tracking
      console.log('📤 AGC: Starting upload...')
      const uploadTask = uploadBytesResumable(storageRef, compressedBlob)
      
      return new Promise((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            setUploadProgress(progress)
            console.log(`📤 AGC: Upload ${progress.toFixed(1)}% complete (${snapshot.bytesTransferred}/${snapshot.totalBytes} bytes)`)
          },
          (error) => {
            console.error('❌ AGC: Upload failed:', error)
            console.error('❌ AGC: Error code:', error.code)
            console.error('❌ AGC: Error message:', error.message)
            reject(error)
          },
          async () => {
            console.log('✅ AGC: Upload complete, getting download URL...')
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
            console.log('✅ AGC: Download URL obtained:', downloadURL)
            resolve(downloadURL)
          }
        )
      })
    } catch (error) {
      console.error('❌ AGC: Upload process failed:', error)
      throw error
    }
  }
  
  // AGC: Remove image
  const handleRemoveImage = async () => {
    if (!editedCrop?.imageUrl) return
    
    if (!confirm('Remover imagem do cultivo?')) return
    
    try {
      // AGC: Delete from storage if exists
      const storage = getStorage()
      const storageRef = ref(storage, editedCrop.imageUrl)
      await deleteObject(storageRef).catch(() => {
        // AGC: Ignore error if file doesn't exist
      })
      
      // AGC: Clear from crop
      setEditedCrop({ ...editedCrop, imageUrl: undefined, imageUpdatedAt: undefined })
      setImagePreview(null)
      setSelectedImage(null)
      
      console.log('🗑️ AGC: Image removed')
    } catch (error) {
      console.error('❌ AGC: Error removing image:', error)
      alert('Erro ao remover imagem')
    }
  }

  // AGC: Handle manual URL input (temporary workaround)
  const handleApplyManualUrl = () => {
    if (!editedCrop || !manualImageUrl.trim()) return
    
    // AGC: Validate URL format
    try {
      new URL(manualImageUrl)
      setEditedCrop({ ...editedCrop, imageUrl: manualImageUrl, imageUpdatedAt: new Date().toISOString() })
      setImagePreview(manualImageUrl)
      console.log('✅ AGC: Manual image URL applied:', manualImageUrl)
    } catch (error) {
      alert('URL inválida. Por favor, insira uma URL completa (ex: https://...)')
    }
  }

  const handleSave = async () => {
    if (!editedCrop) return
    
    try {
      setIsUploading(true)
      
      // AGC: If using manual URL (workaround), skip Firebase upload
      if (useManualUrl && manualImageUrl.trim()) {
        console.log('📝 AGC: Using manual image URL (Firebase workaround):', manualImageUrl)
        editedCrop.imageUrl = manualImageUrl
        editedCrop.imageUpdatedAt = new Date().toISOString()
      }
      // AGC: Upload image if new one selected
      else if (selectedImage) {
        console.log('🚀 AGC: Starting image upload process...')
        const imageUrl = await uploadImage()
        console.log('✅ AGC: Image uploaded successfully:', imageUrl)
        editedCrop.imageUrl = imageUrl
        editedCrop.imageUpdatedAt = new Date().toISOString()
      }
      
      console.log('💾 AGC: Saving crop configuration...')
      onSave(editedCrop)
      onClose()
    } catch (error) {
      console.error('❌ AGC: Error saving crop:', error)
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      alert(`Erro ao salvar cultivo: ${errorMessage}\n\nVerifique o console para mais detalhes.`)
    } finally {
      setIsUploading(false)
      setUploadProgress(0)
    }
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center">
      {/* AGC: Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50" 
        onClick={onClose}
      />
      
      {/* AGC: Modal content */}
      <div className="relative bg-white dark:bg-admin-card-dark rounded-2xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* AGC: Header */}
        <div className="px-6 py-4 border-b border-slate-200 dark:border-white/5 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-200 flex items-center gap-3">
            <span 
              className="w-10 h-10 rounded-lg flex items-center justify-center text-2xl"
              style={{ backgroundColor: `${editedCrop.color}20` }}
            >
              {editedCrop.icon}
            </span>
            Editar {editedCrop.name}
          </h2>
          <button
            onClick={onClose}
            className="text-slate-400 dark:text-slate-500 hover:text-slate-600 text-2xl"
          >
            ×
          </button>
        </div>

        {/* AGC: Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          {/* AGC: Image Upload Section */}
          <div className="mb-6 p-4 bg-info-50 dark:bg-blue-500/15 rounded-xl border border-blue-200 dark:border-white/5">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
              🖼️ Imagem para Cartão LOD
            </h3>
            <p className="text-xs text-slate-600 dark:text-slate-300 mb-3">
              Upload de imagem Full HD (1920px) do cultivo para usar como fundo dos cartões LOD
            </p>
            
            <div className="flex gap-4 items-start">
              {/* AGC: Image Preview */}
              <div className="flex-shrink-0">
                {imagePreview ? (
                  <div className="relative w-48 h-32 rounded-lg overflow-hidden border-2 border-slate-300">
                    <img 
                      src={imagePreview} 
                      alt="Preview"
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={handleRemoveImage}
                      className="absolute top-2 right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      title="Remover imagem"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <div className="w-48 h-32 rounded-lg border-2 border-dashed border-slate-300 dark:border-white/10 flex items-center justify-center bg-slate-50 dark:bg-white/[0.02]">
                    <span className="text-4xl text-slate-400 dark:text-slate-500">📷</span>
                  </div>
                )}
              </div>
              
              {/* AGC: Upload Controls */}
              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleImageSelect}
                  className="hidden"
                />
                
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm font-medium mb-2"
                >
                  📤 {imagePreview ? 'Trocar Imagem' : 'Upload Imagem'}
                </button>
                
                {selectedImage && (
                  <p className="text-xs text-slate-600 dark:text-slate-300 mt-2">
                    Arquivo selecionado: {selectedImage.name} ({(selectedImage.size / 1024).toFixed(1)} KB)
                  </p>
                )}
                
                {isUploading && (
                  <div className="mt-2">
                    <div className="w-full h-2 bg-slate-200 dark:bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-blue-500 transition-all duration-300"
                        style={{ width: `${uploadProgress}%` }}
                      />
                    </div>
                    <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">
                      Enviando... {uploadProgress.toFixed(0)}%
                    </p>
                  </div>
                )}
                
                {/* AGC: TEMPORARY WORKAROUND - Manual URL Input */}
                <div className="mt-4 pt-4 border-t border-slate-200 dark:border-white/5">
                  <div className="flex items-center gap-2 mb-2">
                    <input
                      type="checkbox"
                      id="useManualUrl"
                      checked={useManualUrl}
                      onChange={(e) => setUseManualUrl(e.target.checked)}
                      className="w-4 h-4 text-blue-500 rounded"
                    />
                    <label htmlFor="useManualUrl" className="text-xs text-slate-600 dark:text-slate-300 cursor-pointer">
                      Ou usar URL externa (temporário - sem Firebase)
                    </label>
                  </div>
                  
                  {useManualUrl && (
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={manualImageUrl}
                        onChange={(e) => setManualImageUrl(e.target.value)}
                        placeholder="https://exemplo.com/imagem-cultivo.jpg"
                        className="w-full px-3 py-2 text-sm border border-slate-200 dark:border-white/10 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <button
                        onClick={handleApplyManualUrl}
                        disabled={!manualImageUrl.trim()}
                        className="px-3 py-1.5 bg-green-500 text-white text-xs rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        ✓ Aplicar URL
                      </button>
                      <p className="text-xs text-amber-600">
                        💡 Dica: Use URLs públicas (ex: Unsplash, Pexels) para testar sem Firebase
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        
          {/* AGC: Basic info */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Nome</label>
              <input
                type="text"
                value={editedCrop.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/5 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Ícone</label>
              <input
                type="text"
                value={editedCrop.icon}
                onChange={(e) => handleChange('icon', e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/5 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Cor</label>
              <div className="flex gap-2">
                <input
                  type="color"
                  value={editedCrop.color}
                  onChange={(e) => handleChange('color', e.target.value)}
                  className="w-12 h-10 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={editedCrop.color}
                  onChange={(e) => handleChange('color', e.target.value)}
                  className="flex-1 px-3 py-2 border border-slate-200 dark:border-white/5 rounded-lg focus:ring-2 focus:ring-green-500 font-mono"
                />
              </div>
            </div>
          </div>

          {/* AGC: Climate requirements */}
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
            🌤️ Requisitos Climáticos
          </h3>
          <div className="grid grid-cols-3 gap-4 mb-6 p-4 bg-slate-50 dark:bg-white/[0.02] rounded-xl">
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                Precipitação Total (mm)
              </label>
              <input
                type="number"
                value={editedCrop.precipitationTotal}
                onChange={(e) => handleChange('precipitationTotal', Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/5 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                Temp. Mínima (°C)
              </label>
              <input
                type="number"
                value={editedCrop.tempOptimalMin}
                onChange={(e) => handleChange('tempOptimalMin', Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/5 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                Temp. Máxima (°C)
              </label>
              <input
                type="number"
                value={editedCrop.tempOptimalMax}
                onChange={(e) => handleChange('tempOptimalMax', Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/5 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                Radiação Total (MJ/m²)
              </label>
              <input
                type="number"
                value={editedCrop.radiationTotal}
                onChange={(e) => handleChange('radiationTotal', Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/5 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                Rendimento (t/ha)
              </label>
              <input
                type="number"
                step="0.1"
                value={editedCrop.projectedYield}
                onChange={(e) => handleChange('projectedYield', Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/5 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                Ciclo (dias)
              </label>
              <input
                type="number"
                value={editedCrop.cycleDays}
                onChange={(e) => handleChange('cycleDays', Number(e.target.value))}
                className="w-full px-3 py-2 border border-slate-200 dark:border-white/5 rounded-lg focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          {/* AGC: Growth stages */}
          <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
            📈 Estágios Fenológicos
          </h3>
          <div className="p-4 bg-slate-50 dark:bg-white/[0.02] rounded-xl">
            <div className="grid grid-cols-4 gap-2 mb-2 text-xs font-medium text-slate-500 dark:text-slate-400">
              <div>{t('common.code')}</div>
              <div>{t('common.name')}</div>
              <div>Início (dias)</div>
              <div></div>
            </div>
            {editedCrop.growthStages.map((stage, index) => (
              <div key={index} className="grid grid-cols-4 gap-2 mb-2">
                <input
                  type="text"
                  value={stage.code}
                  onChange={(e) => handleStageChange(index, 'code', e.target.value)}
                  className="px-2 py-1.5 border border-slate-200 dark:border-white/5 rounded text-sm"
                />
                <input
                  type="text"
                  value={stage.name}
                  onChange={(e) => handleStageChange(index, 'name', e.target.value)}
                  className="px-2 py-1.5 border border-slate-200 dark:border-white/5 rounded text-sm"
                />
                <input
                  type="number"
                  value={stage.startDay}
                  onChange={(e) => handleStageChange(index, 'startDay', Number(e.target.value))}
                  className="px-2 py-1.5 border border-slate-200 dark:border-white/5 rounded text-sm"
                />
                <button
                  onClick={() => {
                    const newStages = editedCrop.growthStages.filter((_, i) => i !== index)
                    setEditedCrop({ ...editedCrop, growthStages: newStages })
                  }}
                  className="text-red-500 hover:text-red-600 text-sm"
                >
                  🗑️
                </button>
              </div>
            ))}
            <button
              onClick={() => {
                const newStages = [...editedCrop.growthStages, { code: '', name: '', startDay: 0 }]
                setEditedCrop({ ...editedCrop, growthStages: newStages })
              }}
              className="mt-2 px-3 py-1.5 bg-slate-200 dark:bg-white/10 hover:bg-slate-300 dark:hover:bg-white/[0.02] rounded text-sm font-medium text-slate-600 dark:text-slate-300"
            >
              + Adicionar Estágio
            </button>
          </div>
        </div>

        {/* AGC: Footer */}
        <div className="px-6 py-4 border-t border-slate-200 dark:border-white/5 flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isUploading}
            className="px-5 py-2.5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-white/[0.02] disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={isUploading}
            className="px-5 py-2.5 bg-green-500 text-white rounded-lg hover:bg-green-600 font-medium disabled:opacity-50 flex items-center gap-2"
          >
            {isUploading ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Salvando...
              </>
            ) : (
              <>💾 Salvar Alterações</>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default CropEditModal


